import { isBefore } from 'date-fns'
import { connect, FormikValues } from 'formik'
import { cloneDeep, get, isEmpty } from 'lodash'
import moment from 'moment'
import { IntlShape, useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { array, boolean, date, number, object, string } from 'yup'

import { Radio, Select } from '@/components/Form'
import AudienceTargeting from '@/components/Form/AudienceTargeting'
import FilterSelect from '@/components/Form/FilterSelect'
import { FormLabel, Row } from '@/components/StyledComponents'
import { StyledFrequencyTextContainer, StyledRadioInput } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import FormikEffect from '@/pages/SpaceAppBuilder/FeaturesPage/FormikEffect'
import {
  DailyFrequencyData,
  HourlyFrequencyData,
  MinutesFrequencyData,
  MontlyConciseFrequencyData,
  MontlyVerboseFrequencyData,
  OneTimeFrequencyData,
  RepeatOptions,
  ScheduledTriggerData,
  WeeklyFrequencyData,
  YearlyConciseFrequencyData,
  YearlyVerboseFrequencyData
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import MarkdownParser from '@/utilities/markdownProcessors/MarkdownProcessor'

import Daily from './Daily'
import Hourly from './Hourly'
import messages from './messages'
import scheduledTriggerMessages from './messages'
import Minutes from './Minutes'
import Monthly from './Monthly'
import OneTime from './OneTime'
import { getRepeatOptions, getScheduleText, getScheduleType, getTransformedUIFields, padDateLeft } from './utils'
import Weekly from './Weekly'
import Yearly from './Yearly'

//<editor-fold desc="Interfaces">
type ScheduledTriggerProps = {
  readOnly?: boolean
}
//</editor-fold>

//<editor-fold desc="Styled Components">
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;

  && {
    > * {
      margin-right: 10px;
    }
  }
`

const StyledAudienceRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
  padding-bottom: 1.5rem;

  && {
    > * {
      margin-right: 10px;
    }
  }
`

const StyledSelect = styled(Select)`
  flex-basis: 40%;
`
const StyledRadio = styled(Radio)`
  margin-top: 1rem;
  margin-bottom: 1.5rem !important;
  display: flex;
  flex-direction: row;
  .form_field {
    display: flex;
  }
  @media (max-width: 600px) {
    flex-direction: column;
  }
`

const StyledRadioTab = styled.button<any>`
  background: none;
  color: inherit;
  border: none;
  font: inherit;
  display: flex;
  flex-direction: column;
  padding: 1.15rem;
  box-shadow: rgb(0 0 0 / 20%) 1px 1px 6px;
  flex: 1 0 50%;
  cursor: ${({ disabled }: any) => (disabled ? 'not-allowed' : 'pointer')};
  border-radius: 0.25rem;

  ${props =>
    props.isSelected &&
    css`
      box-shadow: 0 0 0 3px var(--wg-blue-link);
      background-color: #f6f6f6;
    `};
`
//</editor-fold>

const StyledFilterSelect = styled(FilterSelect)`
  .ui.selection.dropdown .menu > .item {
    padding: 0.5rem 1rem !important;
  }
`
const OptionLabel = styled.label`
  font-size: 14px;
  font-weight: normal;
  color: #3a3a3a;
`
const Description = styled.p`
  margin-top: 2px;
  margin-bottom: 5px;
  margin-right: 5px;
  font-size: 0.8rem;
  word-break: break-word;
  white-space: normal;
  color: #6c6c6c;
`

export const isValidStartDateOption = (value: any) => {
  return !isBefore(value, new Date())
}
const isValidStartDateTimeOption = (value: any) => {
  const currentTime = moment().format()
  const valueParsedTime = moment.parseZone(value).format()

  return !moment(valueParsedTime).isBefore(currentTime)
}

const getValidationSchema = (intl: IntlShape, formObject: any) => {
  const result: any = {}

  result.isUserSchedule = boolean().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectScheduleType))
  result.audience = array().when('isUserSchedule', {
    is: isUserSchedule => isUserSchedule === true,
    then: array()
      .of(
        object().shape({
          filters: array().of(
            object().shape({
              attribute: object().required().label('Audience Targeting Attribute'),
              operator: string().required().label('Audience Targeting Operator'),
              value: string().required().label('Audience Targeting Value')
            })
          )
        })
      )
      .required('Audience is required')
      .min(1, 'Audience is required')
  })

  switch (formObject.repeats) {
    case RepeatOptions.OneTime: {
      result.date = date()
        .typeError(intl.formatMessage(scheduledTriggerMessages.pleaseSelectDateTime))
        .required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectDateTime))
        .test(
          'inFuture',
          intl.formatMessage(scheduledTriggerMessages.triggerDateErrorMessage),
          isValidStartDateTimeOption
        )
      break
    }
    case RepeatOptions.Minutes: {
      result.minutes = number().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectMinutes))
      break
    }
    case RepeatOptions.Hourly: {
      result.hours = string().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectHours)).trim()
      break
    }
    case RepeatOptions.Daily: {
      result.time = string()
        .typeError(intl.formatMessage(scheduledTriggerMessages.pleaseSelectTime))
        .required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectTime))
        .trim()
      break
    }
    case RepeatOptions.Weekly: {
      result.days = string().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectDays)).trim()
      result.time = string()
        .typeError(intl.formatMessage(scheduledTriggerMessages.pleaseSelectTime))
        .required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectTime))
        .trim()
      break
    }
    case RepeatOptions.Monthly:
    case RepeatOptions.MonthlyConcise:
    case RepeatOptions.MonthlyVerbose: {
      result.months = string().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectMonth)).trim()
      result.time = string()
        .typeError(intl.formatMessage(scheduledTriggerMessages.pleaseSelectTime))
        .required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectTime))
        .trim()
      result.isVerbose = string().trim()
      result.day = string().when('isVerbose', {
        is: 'false',
        then: string()
          .typeError(intl.formatMessage(scheduledTriggerMessages.pleaseSelectDay))
          .required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectDay))
      })
      result.dayOfWeek = string().when('isVerbose', {
        is: 'true',
        then: string().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectDay))
      })
      result.weekOfMonth = string().when('isVerbose', {
        is: 'true',
        then: string().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectWeek))
      })
      break
    }
    case RepeatOptions.Yearly:
    case RepeatOptions.YearlyConcise:
    case RepeatOptions.YearlyVerbose: {
      result.time = string()
        .typeError(intl.formatMessage(scheduledTriggerMessages.pleaseSelectTime))
        .required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectTime))
        .trim()
      result.isVerbose = string().trim()
      result.dayMonth = string().when('isVerbose', {
        is: 'false',
        then: string()
          .typeError(intl.formatMessage(scheduledTriggerMessages.pleaseSelectDayAndMonth))
          .required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectDayAndMonth))
      })
      result.dayOfWeek = string().when('isVerbose', {
        is: 'true',
        then: string().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectDay))
      })
      result.monthOfYear = string().when('isVerbose', {
        is: 'true',
        then: string().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectMonth))
      })
      result.weekOfMonth = string().when('isVerbose', {
        is: 'true',
        then: string().required(intl.formatMessage(scheduledTriggerMessages.pleaseSelectWeek))
      })
      break
    }
  }
  return result
}

const getInitialValues = (
  triggerData: any,
  configuration: { triggerData: { dayMonth?: boolean; repeats?: boolean } }
) => {
  const { time, dayMonth, repeats, isVerbose } = getTransformedUIFields(configuration)
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const validTimeZone = currentTimeZone || 'UTC'
  const triggerTimeZone = triggerData?.timeZone
  const timeZone = triggerTimeZone ?? validTimeZone

  return {
    repeats,
    date: triggerData.date ?? '',
    minutes: triggerData?.minutes ?? 15,
    months: triggerData?.months,
    hours: triggerData?.hours ?? 6,
    isVerbose,
    time: time,
    day: triggerData?.day,
    days: triggerData?.days ?? [],
    dayMonth,
    dayOfWeek: triggerData?.dayOfWeek,
    weekOfMonth: triggerData?.weekOfMonth,
    monthOfYear: triggerData?.monthOfYear,
    isUserSchedule: triggerData?.isUserSchedule ?? false,
    audience: triggerData?.audience,
    timeZone: timeZone
  }
}

const getScheduledTriggerHelpUrl = 'https://help.workgrid.com/s/article/Scheduled-Trigger'

// triggerData should be ScheduledTriggerData but TS is barking
const getNetworkRequestObject = (data: { triggerData: any; stepData: any }) => {
  const updatedValues: any = cloneDeep(data)
  const { triggerData } = updatedValues

  if (isEmpty(triggerData)) return data

  const minutes = padDateLeft(triggerData.time.getMinutes())
  const hours = padDateLeft(triggerData.time.getHours())
  const time = `${hours}:${minutes}`

  let triggerDataToUpdate = {} as Partial<ScheduledTriggerData>

  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const validTimeZone = currentTimeZone || 'UTC'
  const triggerTimeZone = triggerData?.timeZone
  const timeZone = triggerTimeZone === validTimeZone ? triggerTimeZone : validTimeZone

  switch (triggerData.repeats) {
    case RepeatOptions.OneTime: {
      triggerDataToUpdate = {
        date: new Date(triggerData.date).getFullYear() + '-' + moment(triggerData.date).format('MM-DD HH:mm:ss')
      } as OneTimeFrequencyData
      break
    }
    case RepeatOptions.Minutes: {
      triggerDataToUpdate = {
        minutes: Number.parseInt(triggerData.minutes)
      } as MinutesFrequencyData
      break
    }
    case RepeatOptions.Hourly: {
      triggerDataToUpdate = {
        hours: Number.parseInt(triggerData.hours)
      } as HourlyFrequencyData
      break
    }
    case RepeatOptions.Daily: {
      triggerDataToUpdate = {
        time
      } as DailyFrequencyData
      break
    }
    case RepeatOptions.Weekly: {
      triggerDataToUpdate = {
        time,
        days: triggerData.days
      } as WeeklyFrequencyData
      break
    }
    case RepeatOptions.Monthly: {
      if (triggerData.isVerbose === 'true') {
        triggerDataToUpdate = {
          time,
          repeats: RepeatOptions.MonthlyVerbose,
          dayOfWeek: Number.parseInt(triggerData.dayOfWeek),
          months: Number.parseInt(triggerData.months),
          weekOfMonth: Number.parseInt(triggerData.weekOfMonth)
        } as MontlyVerboseFrequencyData
      } else {
        triggerDataToUpdate = {
          time,
          repeats: RepeatOptions.MonthlyConcise,
          months: Number.parseInt(triggerData.months),
          day: Number.parseInt(triggerData.day)
        } as MontlyConciseFrequencyData
      }
      break
    }
    case RepeatOptions.Yearly: {
      if (triggerData.isVerbose === 'true') {
        triggerDataToUpdate = {
          time,
          repeats: RepeatOptions.YearlyVerbose,
          dayOfWeek: Number.parseInt(triggerData.dayOfWeek),
          monthOfYear: triggerData.monthOfYear,
          weekOfMonth: Number.parseInt(triggerData.weekOfMonth)
        } as YearlyVerboseFrequencyData
      } else {
        const day = padDateLeft(triggerData.dayMonth.getDate())
        const month = padDateLeft(triggerData.dayMonth.getMonth() + 1)
        triggerDataToUpdate = {
          time,
          repeats: RepeatOptions.YearlyConcise,
          dayMonth: `${month}/${day}`
        } as YearlyConciseFrequencyData
      }
      break
    }
  }

  triggerDataToUpdate.timeZone = timeZone
  triggerDataToUpdate.repeats = triggerDataToUpdate.repeats || triggerData.repeats
  triggerDataToUpdate.isUserSchedule = triggerData.isUserSchedule
  if (triggerData.isUserSchedule === true) {
    triggerDataToUpdate.audience = triggerData.audience
  }

  updatedValues.triggerData = triggerDataToUpdate
  return updatedValues
}

const getScheduleTypeOptions = (intl: IntlShape) => {
  return getScheduleType(intl).map((scheduleType: any) => ({
    key: scheduleType.text,
    text: scheduleType.text,
    value: scheduleType?.value,
    content: (
      <div>
        <OptionLabel>{scheduleType.text}</OptionLabel>
        <Description>{scheduleType.description}</Description>
      </div>
    )
  }))
}

const ScheduledTrigger = ({ formik, readOnly = false }: ScheduledTriggerProps & FormikValues) => {
  const intl = useIntl()

  const { values, setFieldValue } = formik

  return (
    <>
      <FormikEffect
        formik={formik}
        onChange={() => {
          setFieldValue('triggerData.timeZone', Intl.DateTimeFormat().resolvedOptions().timeZone)
        }}
      />
      <StyledRow>
        <FormLabel>{intl.formatMessage(messages.scheduleType)}</FormLabel>
      </StyledRow>
      <StyledFilterSelect
        onBlur={() => {}}
        disabled={readOnly}
        name={`triggerData.isUserSchedule`}
        placeholder={intl.formatMessage(messages.selectScheduleType)}
        options={getScheduleTypeOptions(intl)}
        selection={true}
        required
        className="fill-space"
        onChange={(value: unknown) => {
          const targetValue = get(value, 'target.value', false)
          if (targetValue) {
            setFieldValue('triggerData.repeats', RepeatOptions.Daily)
          }
        }}
      />
      {values.triggerData.isUserSchedule === true && (
        <StyledAudienceRow>
          <AudienceTargeting
            readOnly={readOnly}
            required={true}
            rulePrefixText={intl.formatMessage(messages.audienceTargetingRulePrefixText)}
            name="triggerData.audience"
            title={''}
            startWithAudience={true}
            firstFilterMandatory={true}
          />
        </StyledAudienceRow>
      )}
      {values.triggerData.isUserSchedule !== undefined && (
        <>
          <StyledRow>
            <FormLabel>{intl.formatMessage(messages.frequency)}</FormLabel>
          </StyledRow>
          <StyledRadio name="type" disabled={readOnly}>
            {values.triggerData.isUserSchedule !== true && (
              <StyledRadioTab
                disabled={readOnly}
                isSelected={values.triggerData.repeats === RepeatOptions.OneTime}
                onClick={() => setFieldValue('triggerData.repeats', RepeatOptions.OneTime)}
              >
                <StyledRadioInput
                  disabled={readOnly}
                  id={`triggerData.repeats-0`}
                  name="triggerData.repeats"
                  label={intl.formatMessage(messages.oneTime)}
                  value="oneTime"
                  labelLocation="after"
                />
              </StyledRadioTab>
            )}
            <StyledRadioTab
              disabled={readOnly}
              isSelected={values.triggerData.repeats !== RepeatOptions.OneTime}
              onClick={() =>
                values.triggerData.repeats === RepeatOptions.OneTime &&
                setFieldValue('triggerData.repeats', RepeatOptions.Daily)
              }
            >
              <StyledRadioInput
                disabled={readOnly}
                id={`triggerData.repeats-1`}
                label={intl.formatMessage(messages.recurring)}
                name="triggerData.repeats"
                value={
                  values.triggerData.repeats === RepeatOptions.OneTime
                    ? RepeatOptions.Daily
                    : values.triggerData.repeats
                }
                labelLocation="after"
              />
            </StyledRadioTab>
          </StyledRadio>
          {values.triggerData.repeats === RepeatOptions.OneTime ? (
            <OneTime readOnly={readOnly} />
          ) : (
            <>
              <StyledSelect
                disabled={readOnly}
                name={`triggerData.repeats`}
                label={intl.formatMessage(messages.repeats)}
                options={getRepeatOptions(intl)}
                variant={''}
              />
              {values.triggerData.repeats === RepeatOptions.Minutes && (
                <Minutes readOnly={readOnly} isUserSchedule={values.triggerData.isUserSchedule} />
              )}
              {values.triggerData.repeats === RepeatOptions.Hourly && <Hourly readOnly={readOnly} />}
              {values.triggerData.repeats === RepeatOptions.Daily && <Daily readOnly={readOnly} />}
              {values.triggerData.repeats === RepeatOptions.Weekly && <Weekly readOnly={readOnly} />}
              {values.triggerData.repeats === RepeatOptions.Monthly && <Monthly readOnly={readOnly} />}
              {values.triggerData.repeats === RepeatOptions.Yearly && <Yearly readOnly={readOnly} />}
            </>
          )}
          <StyledFrequencyTextContainer>
            {MarkdownParser(getScheduleText(values, intl, false))}
          </StyledFrequencyTextContainer>
        </>
      )}
    </>
  )
}
export default connect(ScheduledTrigger)

export { getInitialValues, getValidationSchema, getNetworkRequestObject, getScheduledTriggerHelpUrl }
